import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Country {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CountryDialogProps {
  open: boolean;
  country: Country | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface CountryFormData {
  name: string;
  code: string;
  isActive: boolean;
}

const CountryDialog: React.FC<CountryDialogProps> = ({
  open,
  country,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CountryFormData>({
    name: '',
    code: '',
    isActive: true
  });
  const [errors, setErrors] = useState<Partial<CountryFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!country;

  // Reset form when dialog opens/closes or country changes
  useEffect(() => {
    if (open) {
      if (country) {
        setFormData({
          name: country.name,
          code: country.code,
          isActive: country.isActive
        });
      } else {
        setFormData({
          name: '',
          code: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [open, country]);

  // Handle input changes
  const handleInputChange = (field: keyof CountryFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };


  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<CountryFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Country name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Country name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Country name cannot exceed 100 characters';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Country code is required';
    } else if (formData.code.trim().length < 2) {
      newErrors.code = 'Country code must be at least 2 characters';
    } else if (formData.code.trim().length > 10) {
      newErrors.code = 'Country code cannot exceed 10 characters';
    } else if (!/^[A-Za-z0-9_-]+$/.test(formData.code.trim())) {
      newErrors.code = 'Country code can only contain letters, numbers, hyphens, and underscores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        isoCode: formData.code.trim().toUpperCase(), // API expects 'isoCode' based on curl example
        ...(isEditing && { id: country.id, active: formData.isActive })  // API expects 'active' field
      };

      const endpoint = isEditing ? 'api/Country/update' : 'api/Country/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} country`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Country' : 'Add New Country'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the country information below'
              : 'Fill in the details to create a new country'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Country Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter country name (e.g., United States, Canada)"
            variant="outlined"
          />

          <TextField
            label="Country Code (ISO)"
            value={formData.code}
            onChange={handleInputChange('code')}
            error={!!errors.code}
            helperText={errors.code}
            disabled={submitting}
            fullWidth
            placeholder="Enter ISO code (e.g., US, CA, UK)"
            variant="outlined"
          />

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleInputChange('isActive')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isActive
                      ? 'This country is currently active and available for use'
                      : 'This country is inactive and will not be available for selection'
                    }
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} Country`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CountryDialog;